import { createReducer, on } from "@ngrx/store";
import { ClinicianDetailsActions, ProviderUploadResults } from "@store/actions/clinician-details/clinician-details.actions";
import { Provider, ProviderTaxonomy } from "@models/provider.model";

export const clinicianDetailsFeatureKey = "clinicianDetails";

export interface State {
  isLoading: boolean;
  providers: Provider[] | null;
  taxonomies: ProviderTaxonomy[] | null;
  error: string | null;
  providerUpload: {
    loading: boolean;
    progress: number;
    error: string | null;
    complete: boolean;
    results: ProviderUploadResults | null;
  };
}

export const initialState: State = {
  isLoading: false,
  providers: null,
  taxonomies: null,
  error: null,
  providerUpload: {
    loading: false,
    progress: 0,
    error: null,
    complete: false,
    results: null
  }
};

export const reducer = createReducer(
  initialState,
  
  on(ClinicianDetailsActions.getOrgProviders, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.getOrgProvidersSuccess, (state, { response }) => ({
    ...state,
    isLoading: false,
    providers: response,
    error: null,
  })),
  on(ClinicianDetailsActions.getOrgProvidersFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.getProviderTaxonomies, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.getProviderTaxonomiesSuccess, (state, { taxonomies }) => ({
    ...state,
    isLoading: false,
    taxonomies,
    error: null,
  })),
  on(ClinicianDetailsActions.getProviderTaxonomiesFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.createProvider, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.createProviderSuccess, (state, { providerId }) => ({
    ...state,
    isLoading: false,
    error: null,
  })),
  on(ClinicianDetailsActions.createProviderFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.updateProvider, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.updateProviderSuccess, (state, { providerId, updatedProvider }) => ({
    ...state,
    isLoading: false,
    error: null,
    providers: state.providers 
      ? state.providers.map(provider => 
          provider.id === providerId ? { ...provider, ...updatedProvider } : provider
        )
      : state.providers,
  })),
  on(ClinicianDetailsActions.updateProviderFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.confirmDeactivateProvider, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.deactivateProviderSuccess, (state, { organizationProviderId }) => ({
    ...state,
    isLoading: false,
    error: null,
    providers: state.providers 
      ? state.providers.map(provider => 
          provider.id === organizationProviderId 
            ? { ...provider, active: 0 } 
            : provider
        )
      : state.providers,
  })),
  on(ClinicianDetailsActions.deactivateProviderFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.linkUserToProvider, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.linkUserToProviderSuccess, (state, { userId, organizationProviderId }) => ({
    ...state,
    isLoading: false,
    error: null,
    providers: state.providers 
      ? state.providers.map(provider => 
          provider.id === organizationProviderId 
            ? { ...provider, userId: userId.toString() } 
            : provider
        )
      : state.providers,
  })),
  on(ClinicianDetailsActions.linkUserToProviderFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.unlinkUserFromProvider, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(ClinicianDetailsActions.unlinkUserFromProviderSuccess, (state, { organizationProviderId }) => ({
    ...state,
    isLoading: false,
    error: null,
    providers: state.providers 
      ? state.providers.map(provider => 
          provider.id === organizationProviderId 
            ? { ...provider, userId: null } 
            : provider
        )
      : state.providers,
  })),
  on(ClinicianDetailsActions.unlinkUserFromProviderFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(ClinicianDetailsActions.uploadProviders, (state) => ({
    ...state,
    providerUpload: {
      ...state.providerUpload,
      loading: true,
      progress: 0,
      error: null,
      complete: false,
      results: null
    }
  })),
  
  on(ClinicianDetailsActions.uploadProvidersProgress, (state, { progress }) => ({
    ...state,
    providerUpload: {
      ...state.providerUpload,
      progress
    }
  })),
  
  on(ClinicianDetailsActions.uploadProvidersSuccess, (state, { results }) => ({
    ...state,
    providerUpload: {
      ...state.providerUpload,
      loading: false,
      complete: true,
      results,
      error: null
    }
  })),
  
  on(ClinicianDetailsActions.uploadProvidersFailure, (state, { error }) => ({
    ...state,
    providerUpload: {
      ...state.providerUpload,
      loading: false,
      error,
      complete: false
    }
  })),
  
  on(ClinicianDetailsActions.clearProviderUploadState, (state) => ({
    ...state,
    providerUpload: {
      loading: false,
      progress: 0,
      error: null,
      complete: false,
      results: null
    }
  }))
);